import storage from '@/services/storage'
import EventBus from '@/eventBus'
import store from '@/vuex/store'
import router from '@/router'

/**
 * Safe localStorage read for this service.
 * storage.ts's getValue calls JSON.parse with no error handling. If a checklist
 * key is corrupted (truncated write, browser extension, storage-limit flush) it
 * would throw on the editor's mount path and break the editor for every reload
 * until the user manually clears storage. We guard against that here rather than
 * patching the shared storage.ts (which is used by unrelated modules).
 */
const safeGetValue = (key, fallback) => {
  try {
    return storage.getValue(key, fallback)
  } catch (_) {
    // Corrupted entry — remove it and return the fallback so the editor stays healthy.
    try { storage.remove(key) } catch (__) { /* ignore if remove also fails */ }
    return fallback
  }
}

export const ONBOARDING_IKEA_CHECKLIST_SOURCE = 'onboarding-ikea-checklist'
export const ONBOARDING_IKEA_CHECKLIST_EVENT = 'onboarding-ikea-checklist:updated'

/**
 * sessionStorage key for the debug override.
 * Set once when ?onboardingIkeaChecklistDebug=1 is seen in the URL;
 * survives client-side redirects that strip query params for the duration of the session.
 * This is intentionally session-scoped (not localStorage) so it never bleeds into
 * production for real users across sessions. The Unleash variant is still the
 * production gate; this key is only a QA/reviewer escape hatch.
 */
export const DEBUG_FLAG_SESSION_KEY = 'onboardingIkeaChecklistDebug'

// In-memory flag: set when the user clicks Close ("X") in the current page session.
// Resets on page reload (intentional — the debug param re-shows after a reload, which
// is the desired QA behaviour). This is NOT persisted; dismissForever() handles persistence.
// isVisible() checks this BEFORE the debug-force-show so an explicit in-session Close
// is always honoured, even when ?onboardingIkeaChecklistDebug=1 is active.
let closedThisSession = false

export const markClosedThisSession = () => {
  closedThisSession = true
}

export const isClosedThisSession = () => closedThisSession

/**
 * Called once at app boot (main.ts) before any router redirects run.
 * If the raw URL contains ?onboardingIkeaChecklistDebug=1, persist it in
 * sessionStorage so that isChecklistEnabled() stays true even after the
 * router strips the param during its internal redirects.
 */
export const captureDebugParamIfPresent = () => {
  try {
    const value = new URLSearchParams(window.location.search).get('onboardingIkeaChecklistDebug')
    if (value === '1') {
      sessionStorage.setItem(DEBUG_FLAG_SESSION_KEY, '1')
    }
  } catch (_) {
    // sessionStorage unavailable — silently ignore
  }
}

export const STEPS = {
  ADD_PRESENTATION: 'add_presentation',
  CREATE: 'create',
  PREVIEW: 'preview',
  PRESENT: 'present',
}

const ALL_STEPS = [STEPS.ADD_PRESENTATION, STEPS.CREATE, STEPS.PREVIEW, STEPS.PRESENT]

const storageKey = (presentationId) => `onboarding-ikea-checklist:${presentationId}`

// Base keys — the actual localStorage key is always suffixed with the logged-in user id
// (see globalKey() below) so that different accounts on the same browser never share state.
export const DISMISSED_FOREVER_KEY = 'onboarding-ikea-checklist:dismissed-forever'
export const GLOBAL_CONFETTI_SHOWN_KEY = 'onboarding-ikea-checklist:confetti-shown'
export const GLOBAL_COMPLETED_STEPS_KEY = 'onboarding-ikea-checklist:completed-steps'

/**
 * Returns the user-scoped version of a global checklist key.
 *
 * Why: without this, every global key is shared across all accounts on the same
 * browser — account B inherits account A's completed steps, confetti state, and
 * "dismissed forever" flag (bug reported by Annie, BOO-69).
 *
 * The Vuex store singleton is already imported by this module, so `currentUser.id`
 * is always available once the user is logged in. Before login (id absent or store
 * not yet populated), we fall back to the bare key — that window is tiny (the
 * checklist is only active when `isChecklistEnabled()` is true, which requires the
 * Unleash variant to be resolved post-login) and means at worst a pre-login flush
 * writes to the unscoped key, which is then ignored once a user id is available.
 */
const globalKey = (baseKey) => {
  const userId = store.state?.currentUser?.id
  return userId ? `${baseKey}:${userId}` : baseKey
}

const isConfettiShownGlobally = () => !!safeGetValue(globalKey(GLOBAL_CONFETTI_SHOWN_KEY), false)

const markConfettiShownGlobally = () => {
  storage.setValue(globalKey(GLOBAL_CONFETTI_SHOWN_KEY), true)
}

const getGlobalCompletedSteps = () => safeGetValue(globalKey(GLOBAL_COMPLETED_STEPS_KEY), {}) || {}

const markStepDoneGlobally = (step) => {
  if (!ALL_STEPS.includes(step)) return
  const steps = getGlobalCompletedSteps()
  if (steps[step]) return
  storage.setValue(globalKey(GLOBAL_COMPLETED_STEPS_KEY), { ...steps, [step]: true })
}

// Returns a copy of state with any globally-completed steps merged in.
const mergeGlobalCompletedSteps = (state) => {
  if (!state) return state
  const global = getGlobalCompletedSteps()
  if (!Object.keys(global).length) return state
  const mergedSteps = { ...state.steps }
  ALL_STEPS.forEach((s) => { if (global[s]) mergedSteps[s] = true })
  return { ...state, steps: mergedSteps }
}

const isDismissedForever = () => !!safeGetValue(globalKey(DISMISSED_FOREVER_KEY), false)

const dismissForever = () => {
  storage.setValue(globalKey(DISMISSED_FOREVER_KEY), true)
  EventBus.$emit(ONBOARDING_IKEA_CHECKLIST_EVENT, null)
}

const defaultState = () => ({
  enrolled: true,
  dismissed: false,
  celebrated: false,
  steps: {
    [STEPS.ADD_PRESENTATION]: false,
    [STEPS.CREATE]: false,
    [STEPS.PREVIEW]: false,
    [STEPS.PRESENT]: false,
  },
})

const isAllDone = (state) => {
  if (!state || !state.steps) return false
  return ALL_STEPS.every((s) => state.steps[s])
}

/**
 * Reads the per-presentation state from localStorage, running any pending
 * schema migrations in-place. The migration writes are a deliberate one-shot
 * side-effect: they run at most once per stale state entry (idempotent once
 * the new fields are present) and bring the entry up to date so every
 * subsequent caller sees a consistent shape. The write is intentional and
 * self-healing rather than a latent bug — it prevents callers from having to
 * defensively handle missing fields on every code path.
 *
 * NOTE: because this function may write to localStorage, it should NOT be
 * called in tight loops or hot computed properties without gating on the
 * migration fields being absent. In practice refreshState() calls it once
 * on component mount / EventBus update, which is acceptable.
 */
const getState = (presentationId) => {
  if (!presentationId) return null
  const state = safeGetValue(storageKey(presentationId), null)
  if (!state) return null
  // Migration: states without add_presentation (pre-step-1) — user already has a
  // presentation open, so the step is clearly done.
  if (state.steps && !(STEPS.ADD_PRESENTATION in state.steps)) {
    const migrated = {
      ...state,
      steps: { [STEPS.ADD_PRESENTATION]: true, [STEPS.CREATE]: true, ...state.steps },
    }
    storage.setValue(storageKey(presentationId), migrated)
    return migrated
  }
  // Migration: states without create (pre-CREATE step).
  if (state.steps && !(STEPS.CREATE in state.steps)) {
    const migrated = { ...state, steps: { [STEPS.CREATE]: true, ...state.steps } }
    storage.setValue(storageKey(presentationId), migrated)
    return migrated
  }
  return state
}

const notify = (presentationId) => {
  EventBus.$emit(ONBOARDING_IKEA_CHECKLIST_EVENT, presentationId)
}

const enroll = (presentationId) => {
  if (!presentationId) return null
  const existing = getState(presentationId)
  if (existing) return existing
  let state = defaultState()
  // Seed with any steps already completed globally (detected by the tracker from
  // server-side signals like firstEventStartedAt, hasCreatedPresentation, etc.)
  state = mergeGlobalCompletedSteps(state)
  storage.setValue(storageKey(presentationId), state)
  notify(presentationId)
  return state
}

const markStepDone = (presentationId, step) => {
  const state = getState(presentationId)
  if (!state || !ALL_STEPS.includes(step) || state.steps[step]) return state
  markStepDoneGlobally(step)
  const next = {
    ...state,
    steps: { ...state.steps, [step]: true },
  }
  storage.setValue(storageKey(presentationId), next)
  notify(presentationId)
  return next
}

const markCelebrated = (presentationId) => {
  const state = getState(presentationId)
  if (!state || state.celebrated) return state
  const next = { ...state, celebrated: true }
  storage.setValue(storageKey(presentationId), next)
  notify(presentationId)
  return next
}

const setCollapsed = (presentationId, collapsed) => {
  const state = getState(presentationId)
  if (!state) return
  storage.setValue(storageKey(presentationId), { ...state, collapsed })
  // No notify — this is a UI-only preference; no re-render needed across components.
}

const dismiss = (presentationId) => {
  const state = getState(presentationId)
  if (!state) return null
  const next = { ...state, dismissed: true }
  storage.setValue(storageKey(presentationId), next)
  notify(presentationId)
  return next
}

const clear = (presentationId) => {
  if (!presentationId) return
  storage.remove(storageKey(presentationId))
  notify(presentationId)
}

/**
 * Returns true when the onboarding checklist should be active for this page load.
 * Three paths, checked in order:
 *   1. Normal: Unleash variant flag 'AHA-42696-onboarding-ikea-checklist-presenter' is on.
 *   2. Debug/sandbox (live param): ?onboardingIkeaChecklistDebug=1 still in the URL.
 *   3. Debug/sandbox (persisted): the param was seen earlier this session before the
 *      Vue router redirected and stripped it. captureDebugParamIfPresent() sets this
 *      in sessionStorage at app boot so the flag survives client-side navigation.
 */
/**
 * True when the debug/QA escape hatch is active for this session — either the
 * ?onboardingIkeaChecklistDebug=1 param is live in the URL, or it was captured
 * into sessionStorage at boot before the router stripped it.
 *
 * This is deliberately SEPARATE from isChecklistEnabled(): the debug flag must
 * be a reliable FORCE-ON for testing, which means it has to override even the
 * "dismissed forever" guard. A reviewer who clicked "X" on a previous sandbox
 * must still be able to see the checklist again just by re-appending the param —
 * otherwise localStorage from a prior round silently suppresses it forever and
 * the tester (correctly) reports "still nothing". Production users never hit
 * this path because the param/sessionStorage flag is never set for them.
 */
export const isDebugForced = () => {
  try {
    if (new URLSearchParams(window.location.search).get('onboardingIkeaChecklistDebug') === '1') {
      return true
    }
    return sessionStorage.getItem(DEBUG_FLAG_SESSION_KEY) === '1'
  } catch (_) {
    return false
  }
}

export const isChecklistEnabled = () => {
  const canUse = store.getters['featureToggles/canUseOnboardingIkeaChecklist']
  if (canUse) return true
  return isDebugForced()
}

/**
 * Called when the presentation editor mounts (renderPresentationDetail becomes true).
 * Enrolls the presentation in the ikea checklist for ALL presentations (unless
 * the user has permanently dismissed the checklist by clicking "X").
 * Also strips the legacy deepLinkSource / onboardingChecklistDebug params from the URL
 * when present — they are no longer required for enrollment but may still arrive via
 * deep-links and should be cleaned up.
 */
const maybeEnrollFromRoute = (presentationId) => {
  if (!isChecklistEnabled()) return
  if (!presentationId) return
  // Skip enrollment if the user has permanently dismissed the checklist —
  // UNLESS the debug param is forcing it on. In that case we still enroll
  // so the component can load its state, but we do NOT clear the dismissed-
  // forever flag: a user's explicit X-click must be truly permanent and must
  // survive debug-param sessions. The isVisible() guard in the component
  // already handles force-show via `isDismissedForever() && !isDebugForced()`,
  // so visibility is correctly overridden without touching the flag.
  if (isDismissedForever()) {
    if (!isDebugForced()) return
    // Debug is on: enroll so state loads, but keep dismissed-forever intact.
  }
  enroll(presentationId)
  // Clean up legacy / debug URL params so they don't persist across refreshes.
  const route = router.currentRoute
  const hasLegacyParams = route.query.deepLinkSource || route.query.onboardingChecklistDebug
  if (hasLegacyParams) {
    const query = { ...route.query }
    delete query.deepLinkSource
    delete query.onboardingChecklistDebug
    router.replace({ query }).catch(() => {})
  }
}

export default {
  STEPS,
  getState,
  enroll,
  markStepDone,
  markCelebrated,
  dismiss,
  clear,
  isAllDone,
  isDismissedForever,
  dismissForever,
  isChecklistEnabled,
  isDebugForced,
  isClosedThisSession,
  markClosedThisSession,
  maybeEnrollFromRoute,
  captureDebugParamIfPresent,
  isConfettiShownGlobally,
  markConfettiShownGlobally,
  getGlobalCompletedSteps,
  markStepDoneGlobally,
  setCollapsed,
}
